import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { capitalize } from '@ember/string';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import type { TaskInstance } from 'ember-concurrency';
import { all, task, dropTaskGroup } from 'ember-concurrency';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import ButtonGroup from 'garaje/components/ui/button-group';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import type Invite from 'garaje/models/invite';
import type RecurringInvite from 'garaje/models/recurring-invite';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { RECURRING_OPTIONS } from 'garaje/utils/enums';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';

interface SendNotificationModalSignature {
  Args: {
    callback: (payload?: unknown) => unknown;
    close?: () => void;
    invites: Array<Invite | null | undefined>;
    action: string;
    onClickBack: () => void;
    prompt: string;
    scope?: string;
    backText?: string;
  };
}

export default class SendNotificationModal extends Component<SendNotificationModalSignature> {
  @service declare ajax: AjaxFetchService;
  @service declare flashMessages: FlashMessagesService;

  @dropTaskGroup saveTaskGroup!: TaskInstance<unknown>;

  @tracked customMessage = '';

  get isShowing(): boolean {
    return isPresent(this.args.callback);
  }

  sendNotificationTask = task({ group: 'saveTaskGroup' }, async () => {
    try {
      await this.args.callback(this.requestPayload);
      await this.sendNotifications();
    } catch (e) {
      this.flashMessages.showFlash('error', parseErrorForDisplay(e));
      throw e;
    } finally {
      this.args.close?.();
    }
  });

  doCallbackTask = task({ group: 'saveTaskGroup' }, async () => {
    await this.args.callback();
    this.args.close?.();
  });

  async sendNotifications(): Promise<unknown> {
    let invites: Array<Invite | RecurringInvite | null | undefined> = this.args.invites;

    if (this.args.scope && RECURRING_OPTIONS.THIS !== this.args.scope) {
      invites = await all(this.args.invites.map((invite) => invite?.recurringInvite));
    }

    return all(
      invites
        // Invite must have an email address or API will throw a 400 error
        .filter((invite) => isPresent(invite?.email))
        .map((invite) => invite?.notifyInvite(this.requestPayload)),
    );
  }

  get requestPayload(): Record<string, unknown> {
    return {
      data: {
        'custom-message': this.customMessage,
        action: this.args.action,
      },
    };
  }

  @action
  setCustomMessage(value: string): void {
    this.customMessage = value;
  }

  <template>
    {{#if this.isShowing}}
      <ModalDialog
        @containerClassNames="ember-modal-dialog--large"
        @targetAttachment="center"
        @translucentOverlay={{true}}
      >
        <FocusTrapper>
          <div data-test-send-notification-modal>
            <div>
              <span class="text-xl font-bold" data-test-header>
                {{@prompt}}
              </span>
            </div>
            <div class="mt-2" data-test-custom-message>
              <textarea
                id="custom-message"
                class="mt-1 resize-y max-h-4"
                placeholder="Include an optional note in your email to your guest"
                value={{this.customMessage}}
                {{on "input" (pick "target.value" this.setCustomMessage)}}
                autocomplete="off"
                data-test-input
              />
            </div>

            <div class="flex">
              <button
                aria-label="back"
                class="btn btn--small btn--hoverFill"
                data-test-back-button
                type="button"
                {{on "click" @onClickBack}}
              >
                {{if @backText @backText "Back"}}
              </button>

              <ButtonGroup class="flex flex-1 justify-end" @reverse={{true}}>
                <button
                  {{on "click" this.sendNotificationTask.perform}}
                  class="btn btn--small btn--brand {{if this.saveTaskGroup.isRunning 'disabled'}}"
                  disabled={{this.saveTaskGroup.isRunning}}
                  type="button"
                  data-test-send-button
                >
                  {{#if this.sendNotificationTask.isRunning}}
                    Sending...
                  {{else}}
                    Send email
                  {{/if}}
                </button>
                <button
                  {{on "click" this.doCallbackTask.perform}}
                  class="btn btn--small btn--outlineFill {{if this.saveTaskGroup.isRunning 'disabled'}}"
                  disabled={{this.saveTaskGroup.isRunning}}
                  type="button"
                  data-test-dont-send-button
                >
                  {{capitalize @action}}, but don't send email
                </button>
              </ButtonGroup>
            </div>
          </div>
        </FocusTrapper>
      </ModalDialog>
    {{/if}}
  </template>
}
